import { useEffect, useRef, useState } from "react";
import ReactEcharts, { EChartsOption } from "echarts-for-react";
import { useAppSelector } from "../../../../app/hooks";
import { miwChartColors, tooltipMark, utahChartColors } from "../../shared/reportUtil";
import {
	TutorialsByCategoryViewModel,
	UsageFilteredByWriterViewModel,
} from "./studentUsageReportApiSlice";

export interface LessonChartProps {
	className?: string;
	categoryId?: number;
	categoryUsage: UsageFilteredByWriterViewModel;
	tutorialUsage: TutorialsByCategoryViewModel[];
	onClick: (categoryId?: number) => void;
}

export function LessonChart(props: LessonChartProps) {
	const { config } = useAppSelector((state) => state.site);
	let chartColors = config?.isUtahComposeSite ? utahChartColors : miwChartColors;

	const { foregroundColor, chartPalette, accentColor } = useAppSelector(
		(state) => state.contrast
	);

	if (chartPalette) {
		chartColors = chartPalette.slice(0, 2);
	}

	const chartRef = useRef<ReactEcharts>(null);

	const [chartHeight, setChartHeight] = useState<number>();

	const getData = () => {
		if (!props.categoryId) {
			// get top-level chart data from categoryUsage
			return (
				props.categoryUsage?.tutorials.map((t) => ({
					name: t.tutorialCategoryName,
					value: t.timeSpentSeconds / 60,
					id: t.tutorialCategoryId,
				})) ?? []
			);
		} else {
			// get drill-down chart data from tutorialUsage
			const categoryUsage = props.tutorialUsage.find(
				(tu) => tu.categoryId == props.categoryId
			);
			return (
				categoryUsage?.tutorials.map((t) => ({
					name: t.tutorialName,
					value: t.timeSpentSeconds / 60,
				})) ?? []
			);
		}
	};

	const getTitle = () => {
		const title = "Lesson Usage By Category";
		if (!props.categoryId) {
			return title;
		} else {
			const categoryUsage = props.tutorialUsage.find(
				(tu) => tu.categoryId == props.categoryId
			);
			return `${title}:  ${categoryUsage?.name ?? "Unknown"}`;
		}
	};
	useEffect(() => {
		const data = getData();
		const title = getTitle();
		setChartHeight(data ? data.length * 60 + 125 : 0);
		const options = updateOptions(
			initialEChartOptions,
			data,
			title,
			chartColors,
			foregroundColor,
			accentColor
		);
		chartRef.current?.getEchartsInstance().setOption(options, false);
	}, [props.categoryId]);

	const onChartClick = (params: any) => {
		props.onClick(params.data.id);
	};

	useEffect(() => {
		// the event handler ReactEcharts provides interferes with
		// transitions when data in the chart changes.
		if (chartRef.current) {
			const instance = chartRef.current.getEchartsInstance();
			instance.on("click", onChartClick);
		}
	}, []);

	return (
		<ReactEcharts
			ref={chartRef}
			option={initialEChartOptions}
			className={`w-full ${props.className}`}
			style={{
				height: chartHeight,
			}}
		/>
	);
}

interface ChartData {
	name: string;
	value: number;
	id?: number;
}

function updateOptions(
	options: EChartsOption,
	data: ChartData[],
	title: string,
	chartColors: string[],
	foregroundColor: string,
	accentColor: string
) {
	options.title.text = title;
	options.title.textStyle = { color: foregroundColor };
	options.textStyle = { color: foregroundColor };
	options.series.data = data.map((d) => ({
		value: d.value,
		id: d.id,
	}));
	options.xAxis.axisLine = {
		show: true,
		lineStyle: { color: foregroundColor },
	};
	((options.xAxis.splitLine = { show: true, lineStyle: { color: accentColor } }),
		(options.yAxis.data = data.map((d) => d.name)));
	options.yAxis.axisLine = { show: true, lineStyle: { color: foregroundColor } };
	options.series.itemStyle = {
		color: function (param: { dataIndex: number }) {
			return chartColors[param.dataIndex % chartColors.length];
		},
	};
	return options;
}

const initialEChartOptions: EChartsOption = {
	textStyle: {
		color: "#333",
		fontFamily: "Montserrat",
		fontWeight: "500",
	},
	title: {
		text: "Lesson Usage By Category",
		padding: [20, 10],
	},
	grid: {
		containLabel: true,
		top: 50,
		left: 30,
		bottom: 25,
	},
	xAxis: {
		type: "value",
		axisLine: {
			show: true,
		},
		name: "Total Time Spent (in minutes)",
		nameLocation: "middle",
		nameGap: 30,
	},
	yAxis: {
		type: "category",
		data: [],
		axisLabel: {
			padding: 25,
			lineHeight: 15,
			interval: 0,
			width: 180,
			overflow: "break",
			hideOverlap: false,
		},
		axisLine: {
			show: true,
		},
	},
	tooltip: {
		trigger: "item",
		formatter: function (params: { value: string; name: string; color: string }) {
			const heading = `<div>${params.name.split("\n")[0]}</div>`;
			const lineItem = `<div>${tooltipMark(params.color)} Time Spent: <b>${params.value}</b> min</div>`;
			return heading + lineItem;
		},
		textStyle: {
			fontSize: 12,
		},
	},
	animationDurationUpdate: 500,
	series: {
		type: "bar",
		id: "category",
		data: [],
		label: {
			show: true,
			position: ["100%", "50%"],
			height: 25,
			width: 25,
			align: "center",
			verticalAlign: "middle",
			padding: 4,
			backgroundColor: "#fff",
			borderRadius: 25,
			borderColor: "inherit",
			borderWidth: 1,
			fontWeight: "bolder",
			fontSize: 12,
		},
		barMinWidth: 40,
		emphasis: {
			itemStyle: {
				shadowBlur: 10,
			},
		},
		universalTransition: {
			enabled: true,
			divideShape: "clone",
		},
	},
};
